import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { ApiError, resetPassword } from "@teamblob/api-client";
import {
  AuthField,
  AuthErrorBanner,
} from "../components/desktop/auth-primitives.js";
import { Button } from "@teamblob/ui/components";

/**
 * `/reset-password?token=…` — set a new password from the email link.
 *
 * Public, standalone. Reads the one-time token from the query, collects a new
 * password (with confirmation), POSTs to /auth/reset-password, then redirects
 * to /login?reset=1. Invalid/expired tokens surface a clear error with a link
 * to request a fresh one.
 */
const searchSchema = z.object({
  // z.coerce.string(): TanStack Router may coerce an all-digit token oddly; be
  // lenient and always treat it as a string.
  token: z.coerce.string().optional(),
});

export const Route = createFileRoute("/reset-password")({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const tokenMissing = token === undefined || token === "";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (tokenMissing) return;
    if (password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setFormError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword({ token: token as string, password });
      void navigate({ to: "/login", search: { reset: "1" } });
    } catch (err) {
      if (err instanceof ApiError && (err.statusCode === 400 || err.isValidation)) {
        setFormError(
          "This reset link is invalid or has expired. Request a new one below.",
        );
      } else {
        setFormError("Something went wrong. Please try again shortly.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-window-face)] p-8 shadow-[var(--shadow-window)]">
        <h1 className="mb-1 text-[22px] font-semibold text-[var(--color-text-strong)]">
          Choose a new password
        </h1>
        <p className="mb-6 text-[13px] text-[var(--color-text-muted)]">
          Enter a new password for your account. You&apos;ll be signed out of
          other sessions.
        </p>

        {tokenMissing ? (
          <div className="mb-4">
            <AuthErrorBanner message="This reset link is missing its token. Please use the link from your email." />
          </div>
        ) : null}

        {formError !== null ? (
          <div className="mb-4">
            <AuthErrorBanner message={formError} />
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <AuthField
            label="New password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            placeholder="At least 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={tokenMissing}
            suffix={
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)]"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />

          <AuthField
            label="Confirm new password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            placeholder="Re-enter your new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={tokenMissing}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            isLoading={submitting}
            disabled={submitting || tokenMissing}
          >
            {submitting ? "Resetting…" : "Reset password"}
          </Button>
        </form>

        <div className="mt-5 border-t border-[var(--color-border-subtle)] pt-4 text-center text-[12px]">
          <Link
            to="/forgot-password"
            className="font-semibold text-[var(--color-text-link)] hover:underline"
          >
            Request a new reset link
          </Link>
        </div>
      </div>
    </main>
  );
}
